import React, { useState } from "react";
import { ReactComponent as ArrowDown } from "../../../shared/assets/Icons/IconArrowDown.svg";
import { ReactComponent as ArrowUp } from "../../../shared/assets/Icons/IconArrowUp.svg";
import { SpeedboxGauge } from "@ui-components/GaugeChart/SpeedboxGauge";
import { formatDataRate, formatDataRateSmart } from "kontrolEdge/helpers/formatDataRate";
import { HorizontalStackedBar } from "@ui-components/StackedBar/GenericStackbar";

interface ServiceUsageChartProps {
  serviceName: string;
  allocated: number;
  consumption: number;
  incomingValue: number;
  outgoingValue: number;
  /**
   * False when the collector returned nothing for this port. The caller used
   * to coalesce a missing reading to 0, which this chart then drew as a real
   * measurement: a gauge at 0% and "In: 0 bps" are indistinguishable from a
   * port carrying no traffic. Absence needs to look like absence.
   */
  hasReading?: boolean;
  colorAllocated?: string;
  colorConsumption?: string;
}

export const ServiceUsageChart: React.FC<ServiceUsageChartProps> = ({
  allocated,
  incomingValue = 0,
  outgoingValue = 0,
  hasReading = true,
}) => {
  const [direction, setDirection] = useState<"out" | "in">("out");

  const incomingMbps = Number.isFinite(incomingValue) ? incomingValue : 0;
  const outgoingMbps = Number.isFinite(outgoingValue) ? outgoingValue : 0;

  const activeMbps = direction === "out" ? outgoingMbps : incomingMbps;

  const pctUsedRaw = allocated > 0 ? (activeMbps / allocated) * 100 : 0;
  const pctUsed = Math.max(0, Math.min(100, pctUsedRaw));
  const pctFree = Math.max(0, 100 - pctUsed);

  return (
    <>
      <div className="pt-1">
        <div className="flex justify-center mb-0.5" role="presentation" onClick={(e) => e.stopPropagation()}>
          <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 gap-0.5">
            {(["out", "in"] as const).map((d) => (
              <button
                key={d}
                className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                  direction === d
                    ? "bg-white text-semantic-text-primary shadow-sm"
                    : "text-semantic-text-secondary hover:text-semantic-text-primary"
                }`}
                onClick={() => setDirection(d)}
              >
                {d === "out" ? "Out" : "In"}
              </button>
            ))}
          </div>
        </div>
        <h3 className="text-semantic-text-primary text-lg text-left font-bold">
          Current Bandwidth Utilization
        </h3>
        <h4 className="text-semantic-text-secondary text-md font-bold mb-4">
          {hasReading ? formatDataRate(activeMbps) : "—"} /{" "}
          {formatDataRate(allocated)}
        </h4>

        {/* GAUGE CHART — replaced by a plain statement when there is no
            reading. A gauge pinned at 0% reads as "no traffic", which is a
            measurement we do not have. */}
        {hasReading ? (
          <div className="w-full flex-wrap items-center justify-center">
            <SpeedboxGauge percent={pctUsed} down={activeMbps} />
          </div>
        ) : (
          <div className="flex h-[120px] w-full items-center justify-center rounded-sm border border-dashed border-semantic-border-secondary bg-semantic-bg-secondary">
            <span className="text-sm font-medium text-semantic-text-secondary">
              No data available
            </span>
          </div>
        )}

        {/* BAR CHART + LABELS */}
        <div className="mt-4 border-t border-gray-300 pt-3">
          <h4 className="text-md text-left font-bold">
            Bandwidth Utilization (%)
          </h4>
          <div className="mt-3 px-5">
            {hasReading && (
              <HorizontalStackedBar
                usedPct={parseFloat(pctUsed.toFixed(1))}
                freePct={parseFloat(pctFree.toFixed(1))}
              />
            )}
            {/* LABELS IN / OUT */}
            <div className="flex justify-between mt-2 text-sm">
              <div className="flex items-center">
                <ArrowDown
                  className={`h-4 w-4 mr-1 ${hasReading ? "text-green-500" : "text-semantic-text-secondary"}`}
                />
                In: {hasReading ? formatDataRateSmart(incomingMbps) : "—"}
              </div>
              <div className="flex items-center">
                <ArrowUp
                  className={`h-4 w-4 mr-1 ${hasReading ? "text-blue-500" : "text-semantic-text-secondary"}`}
                />
                Out: {hasReading ? formatDataRateSmart(outgoingMbps) : "—"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
